import * as React from "react";
import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import useWorkflows from "@/hooks/useWorkflows";
import useRules from "@/hooks/useRules";
import useChatWindows from "@/hooks/useChatWindows";
import useTrackEvent from "@/hooks/useTrackEvent";

type StageColor = "blue" | "amber" | "emerald" | "violet" | "slate";

type Stage = {
  number: number;
  name: string;
  explanation: string;
  toolLabel: string;
  toolPath: string;
  color: StageColor;
  count?: number;
  countLabel?: string;
  triggerBuilt: boolean;
};

const colorClasses: Record<StageColor, { bg: string; border: string; text: string; badge: string; num: string }> = {
  blue: {
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-900",
    badge: "bg-blue-100 text-blue-800",
    num: "bg-blue-600 text-white"
  },
  amber: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-900",
    badge: "bg-amber-100 text-amber-800",
    num: "bg-amber-600 text-white"
  },
  emerald: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-900",
    badge: "bg-emerald-100 text-emerald-800",
    num: "bg-emerald-600 text-white"
  },
  violet: {
    bg: "bg-violet-50",
    border: "border-violet-200",
    text: "text-violet-900",
    badge: "bg-violet-100 text-violet-800",
    num: "bg-violet-600 text-white"
  },
  slate: {
    bg: "bg-slate-50",
    border: "border-slate-200",
    text: "text-slate-900",
    badge: "bg-slate-200 text-slate-800",
    num: "bg-slate-600 text-white"
  }
};

export default function WorkflowsJourneyView() {
  const { workflows, fetch: fetchWorkflows } = useWorkflows();
  const { rules, refresh: refreshRules } = useRules();
  const { chatWindows } = useChatWindows();
  const { track } = useTrackEvent();

  useEffect(() => {
    track("feature_click", "Workflows/JourneyView/open");
    fetchWorkflows();
    refreshRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stages: Stage[] = useMemo(() => {
    const safeWorkflows = Array.isArray(workflows) ? workflows.filter((w) => !w.isTemplate) : [];
    const safeRules = Array.isArray(rules) ? rules : [];
    const safeWindows = Array.isArray(chatWindows) ? chatWindows : [];

    const determineChatCount = safeRules.filter((r) => r.trigger === "determine-chat-window").length;
    const widgetLoadedCount = safeWorkflows.filter((w) => w.triggerType === "widget_loaded" && w.enabled).length;
    const buttonClickedCount = safeWorkflows.filter((w) => w.triggerType === "chat_button_clicked" && w.enabled).length;
    const convoOpenedCount = safeWorkflows.filter((w) => w.triggerType === "conversation_opened" && w.enabled).length;
    const agentUnavailableCount = safeRules.filter(
      (r) => r.trigger === "use-workflow-on-unavailable" || r.trigger === "use-unavailable-survey"
    ).length;
    const postChatCount = safeRules.filter(
      (r) =>
        r.trigger === "use-postchat-survey" ||
        r.trigger === "use-workflow-on-resolve" ||
        r.trigger === "resolve-chat"
    ).length;

    return [
      {
        number: 1,
        name: "Page loads",
        explanation: "Visitor opens a page. Widget-loaded workflows decide whether the chat button appears and which chat window to serve.",
        toolLabel: "Workflows",
        toolPath: "/workflows",
        color: "blue",
        count: widgetLoadedCount + determineChatCount,
        countLabel:
          widgetLoadedCount + determineChatCount === 1
            ? "workflow/rule"
            : "workflows/rules",
        triggerBuilt: true
      },
      {
        number: 2,
        name: "Button clicked",
        explanation: "Visitor clicks to chat. Chat-button-clicked workflows customize the pre-chat form before the conversation opens.",
        toolLabel: "Workflows",
        toolPath: "/workflows",
        color: "amber",
        count: buttonClickedCount + safeWindows.length,
        countLabel:
          buttonClickedCount + safeWindows.length === 1
            ? "workflow/window"
            : "workflows/windows",
        triggerBuilt: true
      },
      {
        number: 3,
        name: "Pre-chat done",
        explanation: "Conversation opens. Route to a bot or directly to a human.",
        toolLabel: "Workflows",
        toolPath: "/workflows",
        color: "emerald",
        count: convoOpenedCount,
        countLabel: convoOpenedCount === 1 ? "workflow" : "workflows",
        triggerBuilt: true
      },
      {
        number: 4,
        name: "During chat",
        explanation: "No-agent fallback and bot → human handoff rules fire while chatting.",
        toolLabel: "Rules",
        toolPath: "/rules",
        color: "violet",
        count: agentUnavailableCount,
        countLabel: agentUnavailableCount === 1 ? "rule" : "rules",
        triggerBuilt: true
      },
      {
        number: 5,
        name: "After chat",
        explanation: "Post-chat surveys, missed-chat follow-ups, and transcript actions.",
        toolLabel: "Rules",
        toolPath: "/rules",
        color: "slate",
        count: postChatCount,
        countLabel: postChatCount === 1 ? "rule" : "rules",
        triggerBuilt: true
      }
    ];
  }, [workflows, rules, chatWindows]);

  return (
    <div className="px-4 py-4">
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 mb-4">
        <div className="font-semibold mb-1">Execution order for every conversation:</div>
        <ol className="list-decimal list-inside space-y-0.5 text-xs">
          <li>Priority Rules fire first (set-conversation-priority) — ALWAYS, no override</li>
          <li>Workflows evaluate by priority (first match wins, lower number = higher priority)</li>
          <li>Always-run workflows fire alongside the exclusive winner (runMode='always')</li>
          <li>Automation/AI Rules fire only if NO workflow matched</li>
          <li>Team routing is the final fallback</li>
        </ol>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 xl:gap-2 relative">
        {stages.map((stage, idx) => {
          const c = colorClasses[stage.color];
          const isLast = idx === stages.length - 1;
          return (
            <div key={stage.number} className="relative">
              <div
                className={`${c.bg} ${c.border} border rounded-lg p-4 h-full flex flex-col min-h-[200px]`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span
                    className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${c.num}`}
                  >
                    {stage.number}
                  </span>
                  <span className={`text-sm font-semibold ${c.text}`}>{stage.name}</span>
                </div>
                <p className={`text-xs ${c.text} opacity-80 flex-1`}>{stage.explanation}</p>

                {stage.count != null && stage.count > 0 && (
                  <div className="mt-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.badge}`}
                    >
                      {stage.count} {stage.countLabel}
                    </span>
                  </div>
                )}

                {!stage.triggerBuilt && (
                  <div className="mt-2">
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-200 text-slate-600 uppercase tracking-wide">
                      Trigger not yet built
                    </span>
                  </div>
                )}

                <div className="mt-3 pt-3 border-t border-black/5">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-0.5">
                    Configured in
                  </div>
                  <Link
                    to={stage.toolPath}
                    className={`text-sm font-medium ${c.text} underline-offset-2 hover:underline inline-flex items-center gap-1`}
                  >
                    {stage.toolLabel}
                    <svg
                      viewBox="0 0 24 24"
                      width="12"
                      height="12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </Link>
                </div>
              </div>

              {!isLast && (
                <div
                  className="hidden xl:flex absolute top-1/2 -right-2 -translate-y-1/2 z-10 items-center justify-center w-4 h-4 text-slate-400"
                  aria-hidden="true"
                >
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
